<script setup lang="ts">
import type { HTMLAttributes } from "vue"
import type { AvatarVariants } from "."
import { UserRound } from "@lucide/vue"
import { computed, ref, watch } from "vue"
import { cn } from "@/shared/lib/utils"
import { avatarVariants, getAvatarPalette, getInitials } from "."

/**
 * DS-аватар (tech-052 + feature-034). Порядок рендеру:
 * 1. `src` непорожній і не сталося `@error` → реальне фото (`<img object-cover>`);
 * 2. інакше є імʼя → ініціали на детермінованому кольоровому тлі (`data-color`,
 *    мапиться в `globals.css` на пару `--avatar-bg/fg`);
 * 3. інакше → нейтральна `UserRound`-іконка на `bg-muted`.
 *
 * Кольорове тло застосовується ЛИШЕ у стані «кольорові ініціали». Стани «фото»
 * (тло перекрите) і «немає імені» (нейтральна іконка) — без кольору.
 */
interface Props {
  /** Імʼя/назва для обчислення ініціалів і кольору тла. Порожнє → fallback. */
  name?: string | null
  /** URL фото. Непорожній і робочий → рендериться поверх плейсхолдера. */
  src?: string | null
  size?: AvatarVariants["size"]
  /** Доступна назва (за замовчуванням — `name`). */
  alt?: string
  class?: HTMLAttributes["class"]
}

const props = withDefaults(defineProps<Props>(), {
  size: "md",
})

const initials = computed(() => getInitials(props.name))
const hasInitials = computed(() => initials.value.length > 0)

/** Фото не вдалося завантажити (битий/404/недоступний URL) → fallback. */
const imgFailed = ref(false)
/** Новий URL — нова спроба показати фото (критично для replace). */
watch(
  () => props.src,
  () => {
    imgFailed.value = false
  },
)

const showPhoto = computed(() => Boolean(props.src) && !imgFailed.value)

/** Індекс кольору тла — лише у стані «кольорові ініціали» (є імʼя, нема фото). */
const colorIndex = computed(() =>
  !showPhoto.value && hasInitials.value
    ? getAvatarPalette(props.name)
    : undefined,
)

/** Доступна назва: явний alt → name → нічого (декоративний плейсхолдер). */
const ariaLabel = computed(() => props.alt ?? props.name ?? undefined)

/** Розмір fallback-іконки кратний діаметру аватара. */
const iconClass = computed(
  () =>
    ({ sm: "size-4", md: "size-5", lg: "size-6", xl: "size-10" })[
      props.size ?? "md"
    ],
)
</script>

<template>
  <span
    data-slot="avatar"
    :data-size="size"
    :data-fallback="hasInitials || showPhoto ? undefined : true"
    :data-color="colorIndex"
    role="img"
    :aria-label="ariaLabel"
    :class="cn(avatarVariants({ size }), props.class)"
  >
    <img
      v-if="showPhoto"
      :src="src ?? undefined"
      alt=""
      loading="lazy"
      data-slot="avatar-image"
      class="size-full object-cover"
      @error="imgFailed = true"
    >
    <span
      v-else-if="hasInitials"
      data-slot="avatar-initials"
      aria-hidden="true"
    >{{ initials }}</span>
    <UserRound
      v-else
      :class="iconClass"
      aria-hidden="true"
    />
  </span>
</template>
